import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Clock,
    Users,
    CheckCircle,
    XCircle,
    RefreshCw,
    Filter,
    ChevronLeft,
    ChevronRight,
    UserCheck,
} from "lucide-react";
import "./AgendaManager.css";
import { API_BASE_URL } from "../../../../api";

import { fetchParamCongesRequest } from "../../../redux/actions/paramCongesActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import { getManagerTeamLeavesRequest } from "../../../redux/actions/leaveRequestsActions";


const DEMO_MODE = false; // 👉 mets false pour repasser en mode normal

const mockTypesAbsences = [
    { id: 1, nom: "Congé annuel", code: "CA", couleur: "#3b82f6" },
    { id: 2, nom: "Congé maladie", code: "CM", couleur: "#ef4444" },
    { id: 3, nom: "Congé sans solde", code: "CSS", couleur: "#f59e0b" },
];

const mockDirectReports = [
    { id: 1, firstName: "Sara", lastName: "El Fassi", profilePictureUrl: "https://i.pravatar.cc/150?img=5" },
    { id: 2, firstName: "Yassine", lastName: "Amrani", profilePictureUrl: "https://i.pravatar.cc/150?img=12" },
    { id: 3, firstName: "Nadia", lastName: "Rahmouni", profilePictureUrl: "https://i.pravatar.cc/150?img=47" },
    { id: 4, firstName: "Omar", lastName: "Idrissi", profilePictureUrl: "https://i.pravatar.cc/150?img=33" },
];

const mockTeamLeaves = [
    { id: 1, employeeId: 1, employeeName: "Sara El Fassi", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-05", dateFin: "2026-08-09", status: "APPROVED", commentaire: "Vacances d'été", duree: 5 },
    { id: 2, employeeId: 2, employeeName: "Yassine Amrani", absenceTypeNom: "Congé maladie", dateDebut: "2026-08-11", dateFin: "2026-08-12", status: "APPROVED", commentaire: "Grippe", duree: 2 },
    { id: 3, employeeId: 3, employeeName: "Nadia Rahmouni", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-17", dateFin: "2026-08-21", status: "PENDING", commentaire: "Voyage", duree: 5 },
    { id: 4, employeeId: 4, employeeName: "Omar Idrissi", absenceTypeNom: "Congé sans solde", dateDebut: "2026-08-24", dateFin: "2026-08-25", status: "CANCELLED", commentaire: "Raison personnelle", duree: 2 },
    { id: 5, employeeId: 1, employeeName: "Sara El Fassi", absenceTypeNom: "Congé annuel", dateDebut: "2026-08-27", dateFin: "2026-08-28", status: "POSTPONED", commentaire: "Reporté", duree: 2 },
];


const ProfileAvatar = ({ collaborator, className = "" }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const picture = String(collaborator?.profilePictureUrl || "").trim();
    const imageUrl = picture
        ? (picture.startsWith("http") ? picture : `${API_BASE_URL}/api/users/images/profile/${picture}`)
        : null;
    const initials = collaborator?.avatar || collaborator?.nom?.split(" ").map((name) => name[0]).join("") || "?";

    return (
        <span className={`amProfileAvatar ${className}`} aria-hidden="true">
            {imageUrl && !imageFailed
                ? <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
                : <span>{initials.slice(0, 2).toUpperCase()}</span>}
        </span>
    );
};

const AgendaManager = () => {
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [selectedCollaborator, setSelectedCollaborator] = useState("all");
    const [selectedType, setSelectedType] = useState("all");

    // === REDUX STATE ===
    const { typesAbsences: reduxTypesAbsences } = useSelector((state) => state.paramConges);
    const { directReports: reduxDirectReports, directReportsLoading } = useSelector((state) => state.user);
    const { teamLeaves: reduxTeamLeaves, loading: leavesLoading } = useSelector((state) => state.leaveRequests);

    const typesAbsences = DEMO_MODE ? mockTypesAbsences : reduxTypesAbsences;
    const directReports = DEMO_MODE ? mockDirectReports : reduxDirectReports;
    const teamLeaves = DEMO_MODE ? mockTeamLeaves : reduxTeamLeaves;

    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchParamCongesRequest());
            if (uid) {
                dispatch(getDirectReportsRequest(uid));
            }
            dispatch(getManagerTeamLeavesRequest());
        }
    }, [dispatch, uid]);

    // === MAPPING DATA ===
    const collaborateurs = useMemo(() => {
        return (directReports || []).map(u => ({
            id: u.id,
            nom: `${u.firstName} ${u.lastName}`,
            avatar: `${u.firstName?.charAt(0) || ""}${u.lastName?.charAt(0) || ""}`,
            profilePictureUrl: u.profilePictureUrl,
        }));
    }, [directReports]);

    const collaborateursParId = useMemo(
        () => new Map(collaborateurs.map((collaborateur) => [String(collaborateur.id), collaborateur])),
        [collaborateurs]
    );

    const conges = useMemo(() => {
        return (teamLeaves || []).map(l => {
            // Mapping status
            let statut = "en_attente";
            if (l.status === "APPROVED") statut = "validé";
            if (l.status === "REJECTED") statut = "refusé";
            if (l.status === "CANCELLED") statut = "annulé";
            if (l.status === "POSTPONED") statut = "reporté";

            return {
                id: l.id,
                collaborateurId: l.employeeId,
                collaborateurNom: l.employeeName,
                type: l.absenceTypeNom,
                debut: l.dateDebut,
                fin: l.dateFin,
                statut: statut,
                motif: l.commentaire,
                duree: l.duree
            };
        });
    }, [teamLeaves]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;

        for (let i = 0; i < startOffset; i++) days.push(null);
        for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
        // Six semaines fixes pour conserver une grille compacte et stable.
        while (days.length < 42) days.push(null);
        return days;
    };

    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);

    const filteredConges = useMemo(() => {
        return conges.filter((c) => {
            if (selectedCollaborator !== "all" && String(c.collaborateurId) !== selectedCollaborator) return false;
            if (selectedType !== "all" && c.type !== selectedType) return false;
            // Un congé est retenu si sa période chevauche celle recherchée.
            if (startDateFilter && c.fin < startDateFilter) return false;
            if (endDateFilter && c.debut > endDateFilter) return false;
            return true;
        });
    }, [conges, selectedCollaborator, selectedType, startDateFilter, endDateFilter]);

    const getCongesForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split("T")[0];
        return filteredConges.filter((c) => c.debut <= dateStr && c.fin >= dateStr);
    };

    const getTypeColor = (typeNom) => {
        const type = (typesAbsences || []).find((t) => t.nom === typeNom);
        return type?.couleur || "#6b7280";
    };

    const changeMonth = (increment) => {
        setCurrentMonth((prev) => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + increment);
            return newDate;
        });
    };

    const selectStartDate = (value) => {
        setStartDateFilter(value);
        if (!value) return;
        const [year, month] = value.split("-").map(Number);
        setCurrentMonth(new Date(year, month - 1, 1));
    };

    const monthName = currentMonth.toLocaleString("fr-FR", { month: "long", year: "numeric" });

    return (
        <div className="amPage">
            <header className="amHeader">
                <div>
                    <h1>Agenda des congés</h1>
                    {/*<p>Suivez les absences et validez les demandes de vos collaborateurs</p>*/}
                </div>
                {(directReportsLoading || leavesLoading) && <div className="amLoader">Chargement...</div>}
            </header>

            <div className="amCard">
                <div className="amBody">
                    {/* Filtres */}
                    <div className="amFilters">
                        <div className="amFilters__label">
                            <Filter size={16} />
                            <span>Filtres :</span>
                        </div>

                        <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="amSelect">
                            <option value="all">Tous les types</option>
                            {(typesAbsences || []).map((type) => (
                                <option key={type.id} value={type.nom}>
                                    {type.nom}
                                </option>
                            ))}
                        </select>

                    </div>

                    <div className="amContentLayout">
                        <aside className="amCollaborators" aria-label="Filtrer par collaborateur">
                            <div className="amCollaborators__title">Collaborateurs</div>
                            <button
                                type="button"
                                className={`amCollaborator ${selectedCollaborator === "all" ? "is-selected" : ""}`}
                                onClick={() => setSelectedCollaborator("all")}
                            >
                                <span className="amAllAvatar"><Users size={15} /></span>
                                <span>Tous les collaborateurs</span>
                            </button>
                            <div className="amCollaborators__list">
                                {collaborateurs.map((collaborateur) => (
                                    <button
                                        key={collaborateur.id}
                                        type="button"
                                        className={`amCollaborator ${selectedCollaborator === String(collaborateur.id) ? "is-selected" : ""}`}
                                        onClick={() => setSelectedCollaborator(String(collaborateur.id))}
                                        title={collaborateur.nom}
                                    >
                                        <ProfileAvatar collaborator={collaborateur} />
                                        <span>{collaborateur.nom}</span>
                                    </button>
                                ))}
                            </div>
                        </aside>

                        <main className="amAgenda">
                            {/* Navigation mois */}
                            <div className="amMonthBar">
                                <div className="amMonthHeading">
                                    <h2 className="amMonthTitle">
                                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                                    </h2>
                                    <label className="amDateSearch">
                                        <span>Du</span>
                                        <input type="date" value={startDateFilter} onChange={(event) => selectStartDate(event.target.value)} aria-label="Date de début du congé" />
                                        <span>Au</span>
                                        <input type="date" value={endDateFilter} min={startDateFilter || undefined} onChange={(event) => setEndDateFilter(event.target.value)} aria-label="Date de fin du congé" />
                                    </label>
                                </div>

                                <div className="amMonthActions">
                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(-1)} title="Mois précédent">
                                        <ChevronLeft size={20} />
                                    </button>

                                    <button type="button" className="amTodayBtn" onClick={() => setCurrentMonth(new Date())}>
                                        Aujourd&apos;hui
                                    </button>

                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(1)} title="Mois suivant">
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Grille calendrier */}
                            <div className="amCalendar">
                                <div className="amWeekHeader">
                                    {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((day) => (
                                        <div key={day} className="amWeekDay">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                <div className="amGrid">
                                    {days.map((date, idx) => {
                                        const congesDuJour = date ? getCongesForDate(date) : [];
                                        const isToday = date && date.toDateString() === new Date().toDateString();
                                        const dayNumber = date ? date.getDate() : "";

                                        return (
                                            <div
                                                key={idx}
                                                className={`amCell ${!date ? "is-empty" : ""} ${isToday ? "is-today" : ""}`}
                                            >
                                                {date && (
                                                    <>
                                                        <div className={`amDayNum ${isToday ? "is-today" : ""}`}>{dayNumber}</div>

                                                        <div className="amCellList">
                                                            {congesDuJour.slice(0, 2).map((conge, i) => {
                                                                const type = (typesAbsences || []).find((t) => t.nom === conge.type);
                                                                const displayName = conge.collaborateurNom || "";
                                                                const collaborator = collaborateursParId.get(String(conge.collaborateurId)) || {
                                                                    nom: displayName,
                                                                    avatar: displayName.split(" ").map((name) => name[0]).join("")
                                                                };

                                                                return (
                                                                    <div
                                                                        key={`${conge.id}-${i}`}
                                                                        className={`amChip ${conge.statut === 'annulé' ? 'is-cancelled' : ''} ${conge.statut === 'reporté' ? 'is-postponed' : ''}`}
                                                                        style={{ backgroundColor: conge.statut === 'validé' || conge.statut === 'en_attente' ? getTypeColor(conge.type) : undefined }}
                                                                        title={`${type?.nom || conge.type} — ${displayName} (${conge.statut})`}
                                                                    >
                                                                        {conge.statut === 'annulé' ? <XCircle size={12} /> : 
                                                                         conge.statut === 'reporté' ? <RefreshCw size={12} /> : 
                                                                         <UserCheck size={12} />}
                                                                        <ProfileAvatar collaborator={collaborator} className="amChip__avatar" />
                                                                        <span className="amChip__code">{type?.code || "???"}</span>
                                                                        <span className="amChip__sep">—</span>
                                                                        <span className="amChip__name">{displayName}</span>
                                                                    </div>
                                                                );
                                                            })}

                                                            {congesDuJour.length > 2 && (
                                                                <div className="amMore">
                                                                    +{congesDuJour.length - 2} autre{congesDuJour.length - 2 > 1 ? "s" : ""}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Légende */}
                            <div className="amLegend">
                                <span className="amLegend__title">Légende :</span>

                                <div className="amLegend__items">
                                    {(typesAbsences || []).map((type) => (
                                        <div key={type.id} className="amLegendItem">
                                            <div className="amLegendDot" style={{ backgroundColor: type.couleur }} />
                                            <span>{type.nom}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="amLegendHint" style={{ display: 'flex', gap: '15px' }}>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }} />
                                        <span style={{ textDecoration: 'line-through', color: '#6b7280' }}>Annulé</span>
                                    </div>
                                    <div className="amLegendItem">
                                        <div className="amLegendDot" style={{ backgroundColor: '#e0f2fe', border: '1px solid #bae6fd' }} />
                                        <span style={{ color: '#0369a1' }}>Reporté</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Users size={16} />
                                        <span>Nom complet affiché</span>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>

                    {/* === LISTE === */}
                    {false && (
                        <div className="amTableWrap">
                            <table className="amTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Type</th>
                                    <th>Période</th>
                                    <th>Durée</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredConges.map((conge) => {
                                    const type = (typesAbsences || []).find((t) => t.nom === conge.type);

                                    return (
                                        <tr key={conge.id}>
                                            <td>
                                                <div className="amCollab">
                                                    <div className="amAvatar">
                                                        {conge.collaborateurNom?.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)}
                                                    </div>
                                                    <span className="amCollabName">{conge.collaborateurNom}</span>
                                                </div>
                                            </td>

                                            <td>
                          <span className="amTypeCode" style={{ backgroundColor: getTypeColor(conge.type) }}>
                            {type?.code}
                          </span>
                                            </td>

                                            <td>
                                                {new Date(conge.debut).toLocaleDateString("fr-FR")} → {new Date(conge.fin).toLocaleDateString("fr-FR")}
                                            </td>

                                            <td className="amStrong">
                                                {conge.duree} jour{conge.duree > 1 ? "s" : ""}
                                            </td>

                                            <td className="amMuted">{conge.motif || "-"}</td>

                                            <td>
                                                <div className="amStatus">
                                                    {conge.statut === "validé" && <CheckCircle size={16} className="amOk" />}
                                                    {conge.statut === "en_attente" && <Clock size={16} className="amWait" />}
                                                    {conge.statut === "refusé" && <XCircle size={16} className="amNo" />}
                                                    {conge.statut === "annulé" && <XCircle size={16} className="amCancel" />}
                                                    {conge.statut === "reporté" && <RefreshCw size={16} className="amPostpone" />}
                                                    <span className="amStatusTxt">{conge.statut.replace("_", " ")}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>

                            {filteredConges.length === 0 && <div className="amEmpty">Aucune absence trouvée pour les filtres sélectionnés.</div>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgendaManager;
